/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components\BackupJobForm.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the maintenance module.
────────────────────────────────────────────────────────────────*/


import { useState, useEffect } from 'react';
import { api } from '../../../../lib/api';
import { getCronProfiles } from '../../../cronProfiles/api';
import { listTenants } from '../../../tenants/api/tenantsApi';
import { useAuthStore } from '../../../../store/auth';
import { useNotifications } from '../../../../contexts/NotificationContext';
import { useI18n } from '../../../../hooks/useI18n';
import { BackupJobConfigBox } from './BackupJobConfigBox';
import { BackupStorageConfigBox } from './BackupStorageConfigBox';
import { BackupHistoryBox } from './BackupHistoryBox';
import { BackupJobActionBar } from './BackupJobActionBar';
import ConfirmationModal from '../../../../components/ui/ConfirmationModal';

// ── Types ─────────────────────────────────────────────────────
interface BackupJobFormProps {
    onCancel: () => void;
    onSaveSuccess: (action?: 'close' | 'new' | 'stay') => void;
    onRun?: (id: number | string) => void;
    initialData?: {
        id?: number | string; name?: string; tenantScope?: string; type?: string;
        targetTenantId?: string | number; tenantId?: string | number; storageType?: string;
        storageConfig?: { bucket?: string; region?: string; accessKey?: string; secretKey?: string; host?: string; port?: string; user?: string; pass?: string; path?: string; };
        cronProfileId?: number | string | null; limitCount?: number;
        includeCdr?: boolean; includeRecordings?: boolean; includeVoicemails?: boolean; includeFaxes?: boolean;
    };
}

// ── Component ─────────────────────────────────────────────────
export default function BackupJobForm({ onCancel, onSaveSuccess, onRun, initialData }: BackupJobFormProps) {
    const { showSuccess: notifySuccess, showError: notifyError } = useNotifications();
    const { user } = useAuthStore();
    const { t } = useI18n();
    const [isSaving, setIsSaving] = useState(false);
    const [cronProfiles, setCronProfiles] = useState<Array<{ id: number | string; name: string; schedule?: string }>>([]);
    const [tenants, setTenants] = useState<Array<{ id: number | string; name: string; slug?: string; telephonyId?: number | string }>>([]);
    const [backups, setBackups] = useState<Array<{ filename: string; createdAt: string; size: number }>>([]);
    const [loadingBackups, setLoadingBackups] = useState(false);

    const [formData, setFormData] = useState({
        name: initialData?.name || '',
        tenantScope: initialData?.tenantScope || (initialData?.type === 'system' ? 'all' : 'specific'),
        tenantId: (initialData?.targetTenantId || '').toString(),
        storageType: initialData?.storageType || 'local',
        storageConfig: initialData?.storageConfig || {},
        cronProfileId: initialData?.cronProfileId ? initialData.cronProfileId.toString() : 'disabled',
        limit: initialData?.limitCount || 10,
        includeCdr: initialData?.includeCdr ?? true,
        includeRecordings: initialData?.includeRecordings ?? true,
        includeVoicemails: initialData?.includeVoicemails ?? true,
        includeFaxes: initialData?.includeFaxes ?? true,
    });

    const [s3Config, setS3Config] = useState({ bucket: initialData?.storageConfig?.bucket || '', region: initialData?.storageConfig?.region || 'us-east-1', accessKey: initialData?.storageConfig?.accessKey || '', secretKey: initialData?.storageConfig?.secretKey || '' });
    const [ftpConfig, setFtpConfig] = useState({ host: initialData?.storageConfig?.host || '', port: initialData?.storageConfig?.port || '21', user: initialData?.storageConfig?.user || '', pass: initialData?.storageConfig?.pass || '', path: initialData?.storageConfig?.path || '/' });

    // ── Load initial data ─────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const cronResult = await getCronProfiles({ limit: 100, enabled: true });
                setCronProfiles(cronResult.data || []);
                const tenantResult = await listTenants(user?.tenant?.slug || '', Number(user?.id) || 0, { page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' });
                if (tenantResult.success && Array.isArray(tenantResult.data)) {
                    setTenants([
                        { id: 'all-tenants', name: 'All Tenants', slug: 'all-tenants', telephonyId: -1 },
                        ...tenantResult.data.map((t: { id: number | string; name: string; slug?: string }) => ({ ...t, telephonyId: t.id })),
                    ]);
                }
            } catch (err) { console.error('Failed to load initial data', err); }
        })();
    }, [user]);

    useEffect(() => { if (initialData?.id) loadBackups(); }, [initialData?.id]);

    const loadBackups = async () => {
        if (!initialData?.id) return;
        setLoadingBackups(true);
        try {
            const res = await api.get(`/api/backup-jobs/${initialData.id}/backups`);
            if (res.data.success) setBackups(res.data.data);
        } catch { notifyError(t('backupRestore:notifications.failedLoad')); }
        finally { setLoadingBackups(false); }
    };

    // ── Handlers ──────────────────────────────────────────────
    const handleChange = (key: string, value: string | number | boolean) =>
        setFormData((prev) => ({ ...prev, [key]: value }));

    const handleTenantChange = (val: number | undefined) => {
        if (val === -1) setFormData((p) => ({ ...p, tenantScope: 'all', tenantId: '' }));
        else setFormData((p) => ({ ...p, tenantScope: 'specific', tenantId: val?.toString() || '' }));
    };

    const handleReset = () => {
        setFormData({ name: initialData?.name || '', tenantScope: initialData?.tenantScope || (initialData?.type === 'tenant' ? 'specific' : 'all'), tenantId: (initialData?.targetTenantId || initialData?.tenantId || '').toString(), storageType: initialData?.storageType || 'local', storageConfig: initialData?.storageConfig || {}, cronProfileId: initialData?.cronProfileId ? initialData.cronProfileId.toString() : 'disabled', limit: initialData?.limitCount || 10, includeCdr: initialData?.includeCdr ?? true, includeRecordings: initialData?.includeRecordings ?? true, includeVoicemails: initialData?.includeVoicemails ?? true, includeFaxes: initialData?.includeFaxes ?? true });
        setS3Config({ bucket: initialData?.storageConfig?.bucket || '', region: initialData?.storageConfig?.region || 'us-east-1', accessKey: initialData?.storageConfig?.accessKey || '', secretKey: initialData?.storageConfig?.secretKey || '' });
        setFtpConfig({ host: initialData?.storageConfig?.host || '', port: initialData?.storageConfig?.port || '21', user: initialData?.storageConfig?.user || '', pass: initialData?.storageConfig?.pass || '', path: initialData?.storageConfig?.path || '/' });
    };

    const handleDownloadBackup = async (filename: string) => {
        try {
            const res = await api.get(`/api/backup-jobs/${initialData?.id}/backups/${filename}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url; link.setAttribute('download', filename);
            document.body.appendChild(link); link.click();
            link.parentNode?.removeChild(link); window.URL.revokeObjectURL(url);
        } catch { notifyError(t('backupRestore:notifications.failedDownload')); }
    };

    const [fileToDelete, setFileToDelete] = useState<string | null>(null);

    const handleDeleteBackupClick = (filename: string) => {
        setFileToDelete(filename);
    };

    const confirmDeleteBackup = async () => {
        if (!fileToDelete) return;
        const filename = fileToDelete;
        setFileToDelete(null);
        try { await api.delete(`/api/backup-jobs/${initialData?.id}/backups/${filename}`); notifySuccess(t('backupRestore:notifications.deleted')); loadBackups(); }
        catch { notifyError(t('backupRestore:notifications.failedDelete')); }
    };

    const handleSaveInternal = async (): Promise<boolean> => {
        setIsSaving(true);
        try {
            const payload = { ...formData, type: formData.tenantScope === 'all' ? 'system' : 'tenant', storageConfig: formData.storageType === 's3' ? s3Config : formData.storageType === 'ftp' ? ftpConfig : {} };
            if (initialData?.id) await api.put(`/api/backup-jobs/${initialData.id}`, payload);
            else await api.post('/api/backup-jobs', payload);
            notifySuccess(t('common.savedSuccessfully')); return true;
        } catch { notifyError(t('common.errorSaving')); return false; }
        finally { setIsSaving(false); }
    };

    const handleSave = async (action: string) => {
        const ok = await handleSaveInternal();
        if (!ok) return;
        if (action === 'save-and-new') { onSaveSuccess('new'); if (!initialData?.id) handleReset(); }
        else if (action === 'save') onSaveSuccess('stay');
        else onSaveSuccess('close');
    };

    // ── Render ────────────────────────────────────────────────
    return (
        <div className="pb-20 space-y-6">
            <BackupJobConfigBox
                formData={formData} tenants={tenants} cronProfiles={cronProfiles}
                onChange={handleChange} onTenantChange={handleTenantChange}
            />
            <BackupStorageConfigBox
                storageType={formData.storageType} s3Config={s3Config} ftpConfig={ftpConfig}
                onStorageTypeChange={(v) => handleChange('storageType', v)}
                onS3Change={(u) => setS3Config((p) => ({ ...p, ...u }))}
                onFtpChange={(u) => setFtpConfig((p) => ({ ...p, ...u }))}
            />
            {initialData?.id && (
                <BackupHistoryBox
                    backups={backups} isLoading={loadingBackups}
                    onDownload={handleDownloadBackup} onDelete={handleDeleteBackupClick}
                />
            )}
            <BackupJobActionBar
                jobId={initialData?.id} isSaving={isSaving}
                onSave={handleSave} onCancel={handleReset} onRun={onRun}
            />

            <ConfirmationModal
                isOpen={!!fileToDelete}
                onClose={() => setFileToDelete(null)}
                onConfirm={confirmDeleteBackup}
                title={t('common.confirmDelete', 'Confirm Deletion')}
                message={t('backupRestore:confirm.deleteBackup', 'Are you sure you want to delete this backup?')}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                type="danger"
            />
        </div>
    );
}
